import type { RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'

interface UseContainerWidthConfig {
  containerElement?: RefObject<HTMLElement | null>
  defaultWidth?: number
  /** Content box doesn't include padding, border, or margin. Border box includes padding and border. */
  boxType?: 'content-box' | 'border-box'
}

/**
 * Hook to get the width of a container element. Responds to changes in the container's width.
 */
export function useContainerWidth({
  containerElement,
  defaultWidth = 300,
  boxType = 'content-box',
}: UseContainerWidthConfig) {
  const [containerWidth, setContainerWidth] = useState(defaultWidth)
  const resizeObserver = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    resizeObserver.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (boxType === 'border-box') {
          setContainerWidth(entry.borderBoxSize[0].inlineSize)
        } else {
          setContainerWidth(entry.contentBoxSize[0].inlineSize)
        }
      }
    })
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect()
      }
    }
  }, [boxType])

  useEffect(() => {
    if (containerElement && containerElement.current) {
      resizeObserver.current?.observe(containerElement.current)
      return () => {
        if (containerElement.current !== null) {
          resizeObserver.current?.unobserve(containerElement.current)
        }
      }
    }
    return undefined
  }, [containerElement])

  return containerWidth
}
